import { useState, useEffect } from 'react';
import { invoiceApi } from '../../api/invoiceApi';

interface InvoiceNumberPreview {
  preview: string | null;
  nextNumber: number;
  format: string;
  message?: string;
}

interface UseInvoiceNumberPreviewResult {
  zugferdPreview: InvoiceNumberPreview | null;
  xrechnungPreview: InvoiceNumberPreview | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook für die Vorschau der nächsten Rechnungsnummern (ZUGFeRD und XRechnung)
 * Lädt automatisch beim Mount und bietet eine Refetch-Funktion
 */
export const useInvoiceNumberPreview = (): UseInvoiceNumberPreviewResult => {
  const [zugferdPreview, setZugferdPreview] = useState<InvoiceNumberPreview | null>(null);
  const [xrechnungPreview, setXrechnungPreview] = useState<InvoiceNumberPreview | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPreviews = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Beide Vorschauen parallel laden
      const [zugferdResult, xrechnungResult] = await Promise.allSettled([
        invoiceApi.previewInvoiceNumber(),
        invoiceApi.previewXRechnungNumber()
      ]);

      // ZUGFeRD-Ergebnis verarbeiten
      if (zugferdResult.status === 'fulfilled') {
        setZugferdPreview(zugferdResult.value);
      } else {
        console.error('Fehler beim Laden der ZUGFeRD-Vorschau:', zugferdResult.reason);
      }

      // XRechnung-Ergebnis verarbeiten
      if (xrechnungResult.status === 'fulfilled') {
        setXrechnungPreview(xrechnungResult.value);
      } else {
        console.error('Fehler beim Laden der XRechnung-Vorschau:', xrechnungResult.reason);
      }

      // Nur Fehler setzen, wenn beide fehlgeschlagen sind
      if (zugferdResult.status === 'rejected' && xrechnungResult.status === 'rejected') {
        setError('Fehler beim Laden der Rechnungsnummer-Vorschauen');
      }

    } catch (err) {
      console.error('Unerwarteter Fehler beim Laden der Vorschauen:', err);
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler');
    } finally {
      setIsLoading(false);
    }
  };

  // Automatisches Laden beim Mount
  useEffect(() => {
    fetchPreviews();
  }, []);

  return {
    zugferdPreview,
    xrechnungPreview,
    isLoading,
    error,
    refetch: fetchPreviews,
  };
}; 